import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {useUserDynamicConfigWithLoading} from '../useDynamicConfig';

export type ProjectInfo = {
  entityName: string;
  projectName: string;
};

export type NgwTruncatedPanelGenLimits = {
  runLimit: number;
  maxPrefixGroups: number;
  maxMetricsPerGroup: number;
  /**
   * Projects eligible for next-gen workspace features.
   * Only listed projects are eligible; when omitted or empty, NGW is off.
   */
  eligibleProjects?: ProjectInfo[];
};

type NgwTruncatedPanelGenLimitsResult = {
  config: NgwTruncatedPanelGenLimits;
  loading: boolean;
};

const DEFAULT_NGW_TRUNCATED_PANEL_GEN_LIMITS: NgwTruncatedPanelGenLimits = {
  runLimit: 100,
  maxPrefixGroups: 50,
  maxMetricsPerGroup: 1_000,
};

/**
 * Returns the matching eligible-project entry, or undefined if the project
 * is not on the allowlist.
 */
export function findNgwEligibleProject(
  entityName: string | undefined,
  projectName: string | undefined,
  eligibleProjects: ProjectInfo[] | undefined
): ProjectInfo | undefined {
  if (
    entityName == null ||
    projectName == null ||
    entityName === '' ||
    projectName === '' ||
    eligibleProjects == null
  ) {
    return undefined;
  }
  return eligibleProjects.find(
    project =>
      project.entityName === entityName && project.projectName === projectName
  );
}

export const useNgwTruncatedPanelGenLimits =
  (): NgwTruncatedPanelGenLimitsResult => {
    const {config, loading} = useUserDynamicConfigWithLoading(
      'ngw_truncated_panel_gen_limits'
    );
    const typedConfig = (config ?? {}) as Partial<NgwTruncatedPanelGenLimits>;
    return {
      config: {
        ...DEFAULT_NGW_TRUNCATED_PANEL_GEN_LIMITS,
        ...typedConfig,
      },
      loading,
    };
  };

/**
 * Whether the current route's project is eligible for NGW features.
 * `loading` is true until Statsig has returned the config — callers that
 * branch NGW vs legacy must wait on it so they don't briefly take the
 * wrong path.
 */
export const useIsCurrentProjectNgwEligible = (): {
  isEligible: boolean;
  loading: boolean;
} => {
  const {entityName, projectName} = useProjectParams();
  const {config, loading} = useNgwTruncatedPanelGenLimits();

  if (loading) {
    return {isEligible: false, loading: true};
  }

  const projectMatch = findNgwEligibleProject(
    entityName,
    projectName,
    config.eligibleProjects
  );

  return {isEligible: projectMatch != null, loading: false};
};
